import { useAuiState } from '@assistant-ui/react';
import { IconMessageExclamation, IconMessageUser } from '@tabler/icons-react';
import { use } from 'react';
import { useTranslation } from 'react-i18next';

import { mantineColorVar } from '#core/lib/formatters/colors.ts';

import {
  type AskUserQuestionInput,
  type AskUserQuestionOutput,
  AskUserQuestionOutputSchema,
  type StepperPayload,
} from './schema.ts';
import { StepperCard } from '#plugin-chat/components/tool-display/StepperCard.tsx';
import { ToolChromeContext } from '#plugin-chat/components/tool-display/ToolChromeContext.ts';
import { ToolContentArea } from '#plugin-chat/components/tool-display/ToolContentArea.tsx';
import { ToolStatusRow } from '#plugin-chat/components/tool-display/ToolStatusRow.tsx';
import { useToolPart } from '#plugin-chat/hooks/useToolPart.ts';

/**
 * Interactive slide stepper for the running/awaitingInput kinds.
 *
 * WORKAROUND: mounted from `running` as well as `awaitingInput` because
 * of an upstream race — the first human() call doesn't flip the part
 * status to `requires-action`, so the UI must show as soon as valid
 * slides exist and no result has settled. Self-guarding: renders nothing
 * outside that window.
 */
export function AskUserQuestionSlides() {
  const threadIsRunning = useAuiState((s) => s.thread.isRunning);
  const { resume, interrupt } = use(ToolChromeContext);
  const { args, result } = useToolPart<AskUserQuestionInput, AskUserQuestionOutput>();

  const slides = args?.slides ?? [];
  const shouldShowUi = !result && slides.length > 0;
  // The interrupt payload carries slideIndex when sent from execute
  const currentSlideIndex = (interrupt?.payload as StepperPayload | undefined)?.slideIndex ?? 0;

  if (!shouldShowUi || !(threadIsRunning || interrupt?.payload)) {
    return null;
  }

  return (
    <ToolContentArea>
      <StepperCard
        currentSlide={currentSlideIndex}
        isStreaming={threadIsRunning}
        onSelect={(slideIndex: number, selectedIds: string[]) => {
          resume?.({ selectedIds, slideIndex });
        }}
        slides={slides}
      />
    </ToolContentArea>
  );
}

/**
 * Completed-question status row: custom icon/color plus a summary of the
 * choices the user made.
 */
export function AskUserQuestionComplete({ result }: { result: unknown }) {
  return (
    <ToolStatusRow color={mantineColorVar('green', 6)} icon={IconMessageUser}>
      <CompletionMessage result={result} />
    </ToolStatusRow>
  );
}

/** Unanswered-question status row (error/cancelled): muted message icon, conventional label. */
export function AskUserQuestionError() {
  return <ToolStatusRow color={mantineColorVar('dark', 3)} icon={IconMessageExclamation} />;
}

interface CompletionMessageProps {
  result: unknown;
}

function CompletionMessage({ result }: CompletionMessageProps) {
  const { t } = useTranslation('chat');

  if (!result) {
    return <div>{t('tools.askUserQuestion.complete')}</div>;
  }

  // Parse result through the output schema to ensure type safety.
  // Errors are thrown (not returned) and rendered by the chrome's Error slot —
  // this component only ever sees the success envelope.
  const parsed = AskUserQuestionOutputSchema.safeParse(result);
  if (parsed.success && parsed.data.data) {
    const dataEntries = Object.entries(parsed.data.data);
    if (dataEntries.length === 0) {
      return <span>{t('tools.askUserQuestion.completeNoChoices')}</span>;
    }

    return (
      <div>
        {dataEntries.map(([title, choices]) => (
          <span key={title}>
            {title} → <strong>{choices.join(', ')}</strong>
          </span>
        ))}
      </div>
    );
  }

  return <div>{t('tools.askUserQuestion.completeChoiceReceived')}</div>;
}
